import { CommonModule, formatDate } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { finalize } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { SessionStorageService } from '../../services/session-storage.service';
import {
  BulkOrderReleaseImportPreview,
  BulkOrderReleaseImportResult,
  BulkOrderReleaseImportRow
} from '../transport-planning.model';
import { TransportPlanningService } from '../transport-planning.service';

@Component({
  selector: 'app-transport-release-bulk-upload',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    TableModule,
    TagModule,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent
  ],
  templateUrl: './transport-release-bulk-upload.component.html',
  styleUrls: ['./transport-release-bulk-upload.component.css']
})
export class TransportReleaseBulkUploadComponent {
  preview?: BulkOrderReleaseImportPreview;
  result?: BulkOrderReleaseImportResult;
  excelFile?: File;
  documentFile?: File;
  message = '';
  isBusy = false;
  importNote = '';

  constructor(
    private service: TransportPlanningService,
    private session: SessionStorageService
  ) {}

  validRows(): BulkOrderReleaseImportRow[] {
    return this.rows().filter(row => row.validationStatus === 'READY');
  }

  rows(): BulkOrderReleaseImportRow[] {
    return this.preview?.rows ?? [];
  }

  previewResultText(): string {
    if (!this.preview) return 'No file previewed';
    return `${this.preview.validRows ?? 0} valid / ${this.preview.invalidRows ?? 0} needs attention`;
  }

  excelFileName(): string {
    return this.excelFile?.name || '';
  }

  documentFileName(): string {
    return this.documentFile?.name || '';
  }

  onExcelFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.excelFile = input.files?.[0] ?? undefined;
  }

  onDocumentFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.documentFile = input.files?.[0] ?? undefined;
  }

  downloadTemplate(): void {
    this.isBusy = true;
    this.service.downloadBulkOrderReleaseTemplate()
      .pipe(finalize(() => this.isBusy = false))
      .subscribe({
        next: blob => this.downloadBlob(blob, 'order-release-bulk-upload-template.xlsx'),
        error: () => this.message = 'Could not download the order release template.'
      });
  }

  previewExcel(): void {
    const companyId = this.companyId();
    if (!companyId || !this.excelFile) {
      this.message = 'Choose a company and Excel workbook first.';
      return;
    }
    this.loadPreview(() => this.service.previewBulkOrderReleaseExcel(this.excelFile!, companyId), 'Excel preview failed.');
  }

  extractDocument(): void {
    const companyId = this.companyId();
    if (!companyId || !this.documentFile) {
      this.message = 'Choose a company and PDF/image document first.';
      return;
    }
    this.loadPreview(() => this.service.extractBulkOrderReleaseDocument(this.documentFile!, companyId), 'AI document extraction failed.');
  }

  createValidRows(): void {
    const companyId = this.companyId();
    const rows = this.validRows();
    if (!companyId || rows.length === 0) {
      this.message = 'There are no valid rows to create.';
      return;
    }
    this.isBusy = true;
    this.message = '';
    this.service.createBulkOrderReleases({ owningCompanyId: companyId, rows })
      .pipe(finalize(() => this.isBusy = false))
      .subscribe({
        next: result => {
          this.result = result;
          this.message = `${result.createdCount ?? 0} order release(s) created. ${result.failedCount ?? 0} failed.`;
        },
        error: () => this.message = 'Could not create order releases from the preview.'
      });
  }

  statusSeverity(status?: string): string {
    return status === 'READY' ? 'success' : 'danger';
  }

  routeLabel(row: BulkOrderReleaseImportRow): string {
    return `${row.originName || 'Origin missing'} to ${row.destinationName || 'Destination missing'}`;
  }

  customerLabel(row: BulkOrderReleaseImportRow): string {
    return [
      row.crmClientNumber ? `Client ${row.crmClientNumber}` : null,
      row.crmClientName || null,
      row.crmAccountName ? `Account ${row.crmAccountName}` : null
    ].filter(Boolean).join(' / ') || 'Customer context missing';
  }

  lineSummary(row: BulkOrderReleaseImportRow): string {
    const lines = row.lines ?? [];
    if (!lines.length) return 'No item lines';
    const quantity = lines.reduce((sum, line) => sum + Number(line.quantity ?? 0), 0);
    const sku = lines.map(line => line.inventoryItemNumber || line.skuNumber || line.itemCode || line.customerItemNumber).filter(Boolean)[0];
    return `${this.formatAmount(quantity)} ${lines[0]?.uom || 'unit'} across ${lines.length} line(s)${sku ? ' / ' + sku : ''}`;
  }

  needBy(row: BulkOrderReleaseImportRow): string {
    const value = row.deliveryWindowEnd || row.pickupWindowStart;
    return value ? formatDate(value, 'dd MMM yyyy HH:mm', 'en-US') : 'Not set';
  }

  firstLineItem(row: BulkOrderReleaseImportRow): string {
    const line = row.lines?.[0];
    return [line?.inventoryItemNumber, line?.skuNumber, line?.itemCode, line?.itemName].filter(Boolean).join(' / ') || 'Item not identified';
  }

  private loadPreview(
    request: () => ReturnType<TransportPlanningService['previewBulkOrderReleaseExcel']>,
    errorMessage: string
  ): void {
    this.isBusy = true;
    this.message = '';
    this.result = undefined;
    request()
      .pipe(finalize(() => this.isBusy = false))
      .subscribe({
        next: preview => {
          this.preview = preview;
          this.message = `${preview.totalRows ?? 0} order release row(s) previewed.`;
        },
        error: () => this.message = errorMessage
      });
  }

  private companyId(): number {
    return this.session.getCompany()?.id ?? 0;
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private formatAmount(value: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
  }
}
